<template>
  <div>
    <div v-if="isChargePending">
      <QuotePaymentHeader paymentType="creditCard" />

      <v-form ref="form" lazy-validation>
        <h2 class="mt-8 grey--text">
          {{ $t('quote.label.cardInfo') }}
        </h2>

        <v-row>
          <v-col>
            <v-text-field
              v-model="credit_card.info.holder_name"
              :rules="[rules.name]"
              :label="$t('forms.nameAsCreditCard')"
              hide-details="auto"
              type="text"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="12">
            <v-text-field
              v-model="credit_card.info.number"
              :rules="[rules.number]"
              :label="$t('forms.creditCardNumber')"
              v-mask="'#### #### #### ####'"
              type="tel"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" :md="version == 2 ? '4' : '6'">
            <v-text-field
              v-model="expire"
              v-mask="'##/####'"
              :error="expiryMonthError"
              :rules="[
                rules.required,
                rules.expiredMonthValidation,
                rules.expiredYearValidation,
              ]"
              :label="$t('forms.cardDate')"
              :hint="$t('onboarding.card.dueDateHint')"
              type="tel"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="6" :md="version == 2 ? '4' : '3'">
            <v-text-field
              v-model="credit_card.info.ccv"
              :rules="[rules.ccv]"
              :label="$t('forms.cardCvv')"
              v-mask="'####'"
              type="tel"
              hide-details="auto"
              hint="Ex.: 123"
              outlined
            />
          </v-col>
          <v-col cols="6" :md="version == 2 ? '4' : '3'">
            <v-select
              v-if="version != 2"
              v-model="installment_amount"
              :label="$t('quote.label.instalmentNumber')"
              :items="installmentNumbers"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            />
            <v-select
              v-else
              v-model="installment_amount"
              :label="$t('quote.label.instalmentNumber')"
              :items="installmentNumbers"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            >
              <template slot="selection" slot-scope="data">
                <span class="font-weight-bold body-2">
                  {{ data.item.installment_amount }}x
                </span>
                <v-spacer class="mr-1" />
                <span class="body-2">
                  {{ formatMoney(data.item.installment_value) }}
                </span>
              </template>
              <template slot="item" slot-scope="data">
                <span class="font-weight-bold body-2">
                  {{ data.item.installment_amount }}x
                </span>
                <v-spacer class="mr-1" />
                <span class="body-2">
                  {{ formatMoney(data.item.installment_value) }}
                </span>
                <v-spacer class="mr-1" />
                <span class="grey--text body-2">
                  {{ formatMoney(data.item.total_value) }}
                </span>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <h2 class="mt-8 grey--text">
          {{ $t('quote.label.personalInfo') }}
        </h2>

        <v-row>
          <v-col>
            <v-text-field
              v-model="credit_card.holder.name"
              :rules="[rules.required]"
              :label="$t('forms.completeName')"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="7">
            <v-text-field
              v-model="credit_card.holder.email"
              :rules="[rules.email]"
              :label="$t('forms.email')"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="credit_card.holder.cpf_cnpj"
              v-mask="
                credit_card.holder.cpf_cnpj.length <= 14
                  ? mask.cpfCnpj.cpf
                  : mask.cpfCnpj.cnpj
              "
              :rules="[rules.documentCheck]"
              :label="$t('forms.cpfCnpj')"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="credit_card.holder.phone"
              v-mask="
                credit_card.holder.phone.length >= 15
                  ? mask.phone.phone9
                  : mask.phone.phone8
              "
              :rules="[rules.phoneValidation]"
              :label="$t('forms.phone')"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="address"
              :label="$t('appointment.formCreate.labels.address')"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="credit_card.holder.address_number"
              :label="$t('appointment.formCreate.labels.streetNumber')"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="credit_card.holder.postal_code"
              v-mask="'#####-###'"
              :label="$t('appointment.formCreate.labels.postalCode')"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <QuotePaymentActions @submit="validate" />
      </v-form>
    </div>

    <QuotePaymentCreditCardSuccess
      v-else
      :successData="successData"
      ref="creditCardSuccess"
    />
  </div>
</template>

<script>
import QuotePaymentHeader from './QuotePaymentHeader'
import QuotePaymentActions from './QuotePaymentActions'
import QuotePaymentCreditCardSuccess from './QuotePaymentCreditCardSuccess'

import {
  name,
  email,
  required,
  documentCheck,
  expiredMonthValidation,
  expiredYearValidation,
  phone as phoneValidation,
} from '@/util/errorHandling'
import { catalogMonetary } from '@/util/catalog'
import { cpfCnpj, phone } from '@/util/masks'
import { mapGetters, mapActions, install } from 'vuex'

export default {
  name: 'QuotePaymentCreditCard',

  components: {
    QuotePaymentHeader,
    QuotePaymentActions,
    QuotePaymentCreditCardSuccess,
  },

  computed: {
    ...mapGetters(['quotes', 'quoteItems', 'successData']),

    hasCharge() {
      return this.quotes.charge
    },

    status() {
      if (!this.hasCharge) return
      return this.quotes.charge.installments[0].status
    },

    isChargePending() {
      if (!this.successData) return true
      return this.successData?.installments[0]?.status === 'PENDING'
    },

    version() {
      return this.quoteItems.version
    },
  },

  methods: {
    ...mapActions(['doQuotePayment']),

    validate() {
      if (this.$refs.form.validate()) {
        const document = this.credit_card.holder.cpf_cnpj.replace(
          /[^0-9]+/g,
          ''
        )
        const cardNumber = this.credit_card.info.number.replace(/[^0-9]+/g, '')
        const expireMonth = this.expire.split('/')[0]
        const expireYear = this.expire.split('/')[1]

        const paymentData = {
          payer_name: this.credit_card.holder.name,
          payer_document: document,
          quote_id: this.quotes.id,
          installment_amount: this.installmentAmount(),
          method: 'ASAAS_CREDIT_CARD',
          credit_card: {
            info: {
              holder_name: this.credit_card.info.holder_name,
              number: cardNumber,
              expiry_month: expireMonth,
              expiry_year: expireYear,
              ccv: this.credit_card.info.ccv,
            },
            holder: {
              name: this.credit_card.holder.name,
              email: this.credit_card.holder.email,
              cpf_cnpj: document,
              postal_code: this.credit_card.holder.postal_code,
              phone: this.credit_card.holder.phone,
              address_number: this.credit_card.holder.address_number,
            },
          },
        }

        this.doQuotePayment(paymentData)
      }
    },

    generateInstallment() {
      for (let i = 1; i <= 10; i++) {
        if (Math.floor(this.quotes.total_by_installments / i) > 5) {
          this.installmentNumbers.push(i);
        }
      }

      if (this.installmentNumbers.length === 0) this.installmentNumbers.push(1);
    },

    cardInstallments() {
      const installments = this.quoteItems.payment_options
      const cardInstallments = installments.filter((installment) => installment.payment_method === 'ASAAS_CREDIT_CARD')
      this.installmentNumbers = cardInstallments
    },

    installmentAmount() {
      return this.version == 2 ? this.installment_amount.installment_amount : this.installment_amount
    },
  },

  data() {
    return {
      formatMoney: catalogMonetary,
      installmentNumbers: [],

      expiryMonthError: false,
      expiryYearError: false,
      installment_amount: 1,
      expire: '',

      address: '',

      credit_card: {
        info: {
          holder_name: null,
          number: null,
          expiry_month: null,
          expiry_year: null,
          ccv: null,
        },

        holder: {
          name: '',
          email: '',
          phone: '',
          cpf_cnpj: '',
          postal_code: '',
          address_number: '',
        },
      },

      rules: {
        name,
        email,
        required,
        documentCheck,
        expiredMonthValidation,
        expiredYearValidation,
        phoneValidation,

        min: (val) => (!!val && val.length >= 7) || 'Obrigatório',
        number: (val) => (!!val && val.length >= 19) || this.$t('rules.required'),
        expiry: (val) => (!!val && val.length >= 7) || this.$t('rules.required'),
        ccv: (val) => (!!val && val.length >= 2) || this.$t('rules.required'),
      },

      mask: { cpfCnpj, phone },
    }
  },

  mounted() {
    if (this.version != 2) this.generateInstallment()
    else this.cardInstallments()
  },
}
</script>
